import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchCartThunk } from "../redux/Slices/cartSlice";

/**
 * useCartSync:
 * Global cross-device and multi-tab cart synchronization hook.
 * Performs periodic smart polling, refetches on window focus/visibility change,
 * and synchronizes across browser tabs via storage events.
 */
export const useCartSync = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    // 1. Immediate sync on mount / route change
    dispatch(fetchCartThunk());

    // 2. Smart Polling Interval (4s on checkout, 12s elsewhere)
    const isCheckout = location.pathname.toLowerCase().includes("checkout");
    const intervalMs = isCheckout ? 4000 : 12000;

    const intervalId = setInterval(() => {
      // Only poll when window/tab is visible
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        dispatch(fetchCartThunk());
      }
    }, intervalMs);

    // 3. Focus & Visibility Handlers
    const handleFocusOrVisibility = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        dispatch(fetchCartThunk());
      }
    };

    // 4. Cross-tab Storage Listener
    const handleStorageChange = (e) => {
      if (!e.key || e.key === "state" || e.key === "total" || e.key === "orderId") {
        dispatch(fetchCartThunk());
      }
    };

    window.addEventListener("focus", handleFocusOrVisibility);
    document.addEventListener("visibilitychange", handleFocusOrVisibility);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocusOrVisibility);
      document.removeEventListener("visibilitychange", handleFocusOrVisibility);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch, location.pathname]);
};

export default useCartSync;
